import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Cross1Icon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { closePuckDrawer, usePuckDrawer } from "./puck-drawer-store";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

/**
 * Host UNIQUE des drawers de champs CMS avancé. Monté HORS de `<Puck>` (sibling
 * dans editor.tsx, PAS dans overrides.puck) → totalement découplé de l'arbre de
 * rendu de Puck (le drawer survit aux re-render/remount de l'éditeur).
 *
 * ⚠️ Volontairement SANS Radix Dialog / `StyledDrawer` : la Dialog Radix
 * (FocusScope / DismissableLayer / RemoveScroll / injection de styles) faisait
 * réagir l'iframe de Puck (auto-zoom via ResizeObserver → recalcul de hauteur)
 * → le canva se « rechargeait » et remontait en haut à CHAQUE ouverture de
 * drawer. Ici, panneau fixe simple portalé dans `document.body` : aucun effet de
 * bord global (pas de scroll-lock, pas de focus-trap, pas de mutation du head).
 * Le canva reste en place. Contenu fourni par le champ via `openPuckDrawer`.
 */
export function PuckDrawerHost() {
  const cmsNew = useCmsNew();
  const { open, title, maxWidth, node } = usePuckDrawer();

  // Échap ferme le drawer (le seul comportement Radix qu'on garde).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePuckDrawer();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="false"
      aria-label={typeof title === "string" ? title : undefined}
      className={cn(
        "fixed inset-y-0 right-0 z-[100] flex w-full flex-col border-l bg-white shadow-xl",
        "duration-300 animate-in slide-in-from-right",
        maxWidth || "sm:max-w-sm",
      )}
    >
      <div className="flex items-center gap-4 p-6 pb-4">
        <div className="grow text-lg font-semibold leading-normal text-[#0c111d] font-['Raleway']">
          {title}
        </div>
        <button
          type="button"
          onClick={() => closePuckDrawer()}
          className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label={cmsNew("cms.new.auto.label.fermer", "Fermer")}
        >
          <Cross1Icon className="h-4 w-4" />
        </button>
      </div>
      <div className="border-t" />
      {/* pt-0 : un padding-top ferait défiler le contenu AU-DESSUS des
          barres sticky (gap visible). Chaque body gère son pt si besoin. */}
      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-4 pt-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {node}
      </div>
    </div>,
    document.body,
  );
}
